import { ProductType } from "elephant-web/gql";
import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_NON_PHARMA_ITEM_COLLECTION_NAME = "non-pharma-item";

export const useRxCollectionNonPharmaItem =
  (): RxCollection<NonPharmaItem> | null =>
    useRxCollection<NonPharmaItem>(RXDB_NON_PHARMA_ITEM_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<NonPharmaItem>>;
export type NonPharmaItemQueryConstructor = UseRxData["1"];

const NON_PHARMA_ITEM_SCHEMA_LITERAL = {
  title: "non-pharma item schema",
  // Bumped from 0 to 1 when `brand` and `snomedId` were widened from
  // `type: "string"` to `type: ["string", "null"]` — without the bump,
  // any user who had already replicated the v0 schema would hit
  // RxError (DB6) on next load. Migration is a no-op since the only
  // difference is value-nullability, which Ajv accepts both ways.
  version: 1,
  description: "Represents a NonPharmaItem from the clinical-product catalogue",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    meta: SYNCABLE_META,
    productType: {
      type: "string",
      enum: Object.values(ProductType),
    },
    title: {
      type: "string",
    },
    brand: {
      type: ["string", "null"],
    },
    categoryId: {
      type: "string",
    },
    snomedId: {
      type: ["string", "null"],
    },
    keywords: {
      type: "array",
      items: {
        type: "string",
      },
    },
  },
  required: ["id", "meta", "title", "keywords", "categoryId", "productType"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(NON_PHARMA_ITEM_SCHEMA_LITERAL);

export type NonPharmaItem = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const NON_PHARMA_ITEM_SCHEMA: RxJsonSchema<NonPharmaItem> =
  NON_PHARMA_ITEM_SCHEMA_LITERAL;
